export interface ModuleCatalogItem {
    module_key: string;
    module_name: string;
    route?: string;
    visible?: boolean;
}

export const DEFAULT_MODULE_CATALOG: ModuleCatalogItem[] = [
    { module_key: 'dashboard', module_name: 'Analytics Dashboard' },
    { module_key: 'generation_reports', module_name: 'Generation Reports' },
    { module_key: 'om', module_name: 'O&M' },
    { module_key: 'upload', module_name: 'Upload Excel File' },
    { module_key: 'alerts', module_name: 'Alerts Portal' },
    { module_key: 'simulation', module_name: 'Simulation-on-Demand' },
    { module_key: 'dsm', module_name: 'DSM' },
    { module_key: 'inventory', module_name: 'Inventory Management' },
    { module_key: 'vendors', module_name: 'Vendor Management' },
    { module_key: 'documentation', module_name: 'Documentation' },
    { module_key: 'compliance', module_name: 'Compliance' },
];

export const buildPermissionKey = (moduleKey: string): string => `${moduleKey}.view`;

export const getModuleSelectionMap = (
    modules: ModuleCatalogItem[],
    selectedModuleKeys: string[] = []
): Record<string, boolean> => {
    const selectedSet = new Set(selectedModuleKeys);
    const map: Record<string, boolean> = {};
    modules.forEach((module) => {
        map[module.module_key] = selectedSet.size > 0 ? selectedSet.has(module.module_key) : true;
    });
    return map;
};

export const loadModuleCatalog = async (backendApi: any): Promise<ModuleCatalogItem[]> => {
    const response = await backendApi.getNavigationModules();
    if (response?.success && Array.isArray(response.modules) && response.modules.length > 0) {
        return response.modules.map((item: any) => ({
            module_key: item.module_key,
            module_name: item.module_name,
            route: item.route,
            visible: item.visible,
        }));
    }
    return DEFAULT_MODULE_CATALOG;
};

export const applyCompanyRolePolicies = async (
    backendApi: any,
    params: {
        companyId: number | string;
        moduleSelections: Record<string, boolean>;
        roleIds: number[];
    }
): Promise<void> => {
    const { companyId, moduleSelections, roleIds } = params;
    const normalizedCompanyId = Number(companyId);
    if (!normalizedCompanyId || !roleIds.length) return;

    const jobs: Promise<any>[] = [];
    Object.entries(moduleSelections).forEach(([moduleKey, isEnabled]) => {
        roleIds.forEach((roleId) => {
            jobs.push(
                backendApi.upsertNavigationRolePolicy({
                    role_id: roleId,
                    permission_key: buildPermissionKey(moduleKey),
                    scope_level: 'company',
                    company_id: normalizedCompanyId,
                    effect: isEnabled ? 'allow' : 'deny',
                    is_active: true,
                })
            );
        });
    });

    await Promise.all(jobs);
};

export const applySiteRolePolicies = async (
    backendApi: any,
    params: {
        companyId: number | string;
        siteId: number | string;
        moduleSelections: Record<string, boolean>;
        roleIds: number[];
    }
): Promise<void> => {
    const { companyId, siteId, moduleSelections, roleIds } = params;
    const normalizedCompanyId = Number(companyId);
    const normalizedSiteId = Number(siteId);
    if (!normalizedCompanyId || !normalizedSiteId || !roleIds.length) return;

    const jobs: Promise<any>[] = [];
    Object.entries(moduleSelections).forEach(([moduleKey, isEnabled]) => {
        roleIds.forEach((roleId) => {
            jobs.push(
                backendApi.upsertNavigationRolePolicy({
                    role_id: roleId,
                    permission_key: buildPermissionKey(moduleKey),
                    scope_level: 'site',
                    company_id: normalizedCompanyId,
                    site_id: normalizedSiteId,
                    effect: isEnabled ? 'allow' : 'deny',
                    is_active: true,
                })
            );
        });
    });

    await Promise.all(jobs);
};

export const applyUserOverrides = async (
    backendApi: any,
    params: {
        userId: number | string;
        companyId?: number | string | null;
        siteId?: number | string | null;
        moduleSelections: Record<string, boolean>;
    }
): Promise<void> => {
    const { userId, companyId, siteId, moduleSelections } = params;
    const normalizedUserId = Number(userId);
    if (!normalizedUserId) return;

    const normalizedCompanyId = companyId ? Number(companyId) : null;
    const normalizedSiteId = siteId ? Number(siteId) : null;

    const jobs: Promise<any>[] = [];
    Object.entries(moduleSelections).forEach(([moduleKey, isEnabled]) => {
        jobs.push(
            backendApi.upsertNavigationUserOverride({
                user_id: normalizedUserId,
                permission_key: buildPermissionKey(moduleKey),
                scope_level: 'user',
                company_id: normalizedCompanyId,
                site_id: normalizedSiteId,
                effect: isEnabled ? 'allow' : 'deny',
                reason: 'Saved from admin user form',
                is_active: true,
            })
        );
    });

    await Promise.all(jobs);
};

export const applyCompanySubscriptions = async (
    backendApi: any,
    params: {
        companyId: number | string;
        moduleSelections: Record<string, boolean>;
        durationMonths: number;
    }
): Promise<void> => {
    const { companyId, moduleSelections, durationMonths } = params;
    const normalizedCompanyId = Number(companyId);
    const normalizedDuration = Number(durationMonths) > 0 ? Number(durationMonths) : 12;
    if (!normalizedCompanyId) return;

    const startAt = new Date();
    startAt.setHours(0, 0, 0, 0);
    const endAt = new Date(startAt);
    endAt.setMonth(endAt.getMonth() + normalizedDuration);

    const jobs: Promise<any>[] = [];
    Object.entries(moduleSelections).forEach(([moduleKey, isEnabled]) => {
        jobs.push(
            backendApi.upsertNavigationSubscription({
                company_id: normalizedCompanyId,
                module_key: moduleKey,
                status: isEnabled ? 'active' : 'suspended',
                start_at: startAt.toISOString(),
                end_at: endAt.toISOString(),
                source: 'admin_grant',
                auto_renew: false,
            })
        );
    });

    await Promise.all(jobs);
};
